import { useContext, useState } from 'react';
import { translateFault, generateUnhandledFault } from '../../helpers/faults';
import {
  submitModifyOrderUpdateCountryUrl,
  submitOrderUpdateCountryUrl,
} from '../../constants/checkout.constant';
import { WebApiContext } from 'ascendon-web-api';
import externalReferences from '../../constants/externalReferences';

function useSubmitOrderUpdateCountry() {
  const [fault, setFault] = useState(null);
  const [interactionData, setInteractionData] = useState(null);
  const [uiModel, setUiModel] = useState(null);
  const [loading, setLoading] = useState(null);
  const WebApi = useContext(WebApiContext);

  const additionalProperties = (isUpgrade, additionalPropertiesData = []) => {
    const additionalPropertiesExcludingLeadSource =
      additionalPropertiesData.filter(
        (item) => item.ExternalReference !== externalReferences.LEAD_SOURCE,
      );
    if (!isUpgrade) {
      return additionalPropertiesExcludingLeadSource;
    }
    return [
      ...additionalPropertiesExcludingLeadSource,
      {
        ExternalReference: externalReferences.METHOD,
        Values: [externalReferences.METHOD_VALUE],
      },
    ];
  };

  const getApiParams = ({ staticApiUrl, staticApiKey, isUpgrade }) => {
    const baseURL = `${staticApiUrl}`;
    const generalRequestHeaders = {
      name: 'Content-Type',
      value: 'application/json; charset=utf-8',
    };
    const setApiKey = staticApiKey?.length
      ? {
          add: [
            { name: 'apikey', value: staticApiKey },
            { ...generalRequestHeaders },
          ],
        }
      : {
          add: [{ ...generalRequestHeaders }],
        };

    const headers = {
      ...setApiKey,
    };
    const staticApiSubmitOrderUpdateCountryUrl = `${baseURL}${submitOrderUpdateCountryUrl}`;
    if (isUpgrade) {
      const submitModifyOrderUpdateCountryCustomUrl = `${baseURL}${submitModifyOrderUpdateCountryUrl}`;
      return { headers, submitModifyOrderUpdateCountryCustomUrl };
    }

    return {
      staticApiSubmitOrderUpdateCountryUrl,
      headers,
    };
  };

  const submitModifyOrderPayload = ({
    ShoppingCart,
    PaymentInstruments,
    SubscriptionItemId,
    SubscriptionId,
    RedemptionCodes,
    ThreeDSDetail,
    AdditionalProperties,
  }) => ({
    ReplaceItems: [
      {
        PricingPlanId: ShoppingCart.Items?.[0]?.PricingPlanId,
        ProductId: ShoppingCart.Items?.[0]?.ProductId,
        SubscriptionItemId,
      },
    ],
    PaymentInstrumentIds: [...PaymentInstruments.map((item) => item.Id)],
    SubscriptionId,
    ChangeImmediately: true,
    RedemptionCodes,
    ThreeDSDetail,
    AdditionalProperties: additionalProperties(true, AdditionalProperties),
  });

  const requestSubmitOrderUpdateCountry = (payload) => {
    const {
      submitOrderUpdateCountryPayload,
      staticApiKey,
      staticApiUrl,
      isUpgrade,
      SubscriptionId,
      SubscriptionItemId,
    } = payload;
    const {
      staticApiSubmitOrderUpdateCountryUrl,
      submitModifyOrderUpdateCountryCustomUrl,
      headers,
    } = getApiParams({
      staticApiUrl,
      staticApiKey,
      isUpgrade,
    });
    //const callName = 'SubmitOrder';
    setFault(null);
    setLoading(true);

    const {
      ShoppingCart,
      PaymentInstruments,
      RedemptionCodes,
      ThreeDSDetail,
      AdditionalProperties,
    } = submitOrderUpdateCountryPayload || {};

    const getWebApi = isUpgrade
      ? WebApi.subscriber({
          forceRefresh: true,
          payload: submitModifyOrderPayload({
            ShoppingCart,
            PaymentInstruments,
            SubscriptionItemId,
            SubscriptionId,
            RedemptionCodes,
            ThreeDSDetail,
            AdditionalProperties,
          }),
          customUrl: submitModifyOrderUpdateCountryCustomUrl,
          headers,
        })
      : WebApi.subscriber({
          // callName,
          // keyFactors: [callName],
          forceRefresh: true,
          payload: {
            ...submitOrderUpdateCountryPayload,
            AdditionalProperties: additionalProperties(
              false,
              AdditionalProperties,
            ),
          },
          customUrl: staticApiSubmitOrderUpdateCountryUrl,
          headers,
        });

    getWebApi
      .then((response) => {
        const submitOrderUpdateCountryResult = response?.data;
        if (
          !submitOrderUpdateCountryResult?.Fault &&
          !submitOrderUpdateCountryResult?.fault
        ) {
          if (isUpgrade) {
            setTimeout(() => {
              setUiModel({
                submitOrderUpdateCountryResult,
              });
              setLoading(false);
            }, 2000);
          } else {
            setUiModel({
              submitOrderUpdateCountryResult,
            });
            setLoading(false);
          }
        } else {
          setLoading(false);
          setInteractionData({
            description: 'Web: Submit Order Api Failed',
            requestPayload: submitOrderUpdateCountryPayload,
            errorStack: response,
          });
          if (submitOrderUpdateCountryResult?.Fault?.Code !== 635) {
            let errorMsg;
            const submitOrderError617 = {
              Code: 617,
            };
            if (submitOrderUpdateCountryResult?.Fault?.Subcode) {
              const defaultFault = {
                Code: 617,
                Subcode: 6170124,
              };
              const getSubmitOrderFaultMessage = (faultSubcode) => {
                const faultMessage = {
                  6170124: () =>
                    translateFault(submitOrderUpdateCountryResult.Fault)
                      .translatedMessage,

                  default: () =>
                    translateFault({ ...defaultFault }).translatedMessage,
                };
                return (
                  faultMessage[faultSubcode] || faultMessage['default']
                )();
              };

              errorMsg = getSubmitOrderFaultMessage(
                submitOrderUpdateCountryResult.Fault.Subcode,
              );
              setFault(errorMsg);
            } else {
              errorMsg = translateFault(submitOrderError617).translatedMessage;
              setFault(errorMsg);
            }
          } else {
            setFault(submitOrderUpdateCountryResult);
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        console.warn({ error });
        const code = error?.data?.Code;
        let errorMsg;
        if (code !== 635) {
          setInteractionData({
            description: 'Web: Submit Order Failed',
            requestPayload: submitOrderUpdateCountryPayload,
            errorStack: error,
          });
        }
        if (code === 635) {
          setFault({
            Fault: error?.data,
          });
        } else if (!code) {
          const unknownFault = generateUnhandledFault(error.data);
          errorMsg = translateFault(unknownFault).translatedMessage;
          setFault(errorMsg);
        } else {
          errorMsg = translateFault(error.data).translatedMessage;
          setFault(errorMsg);
        }
      });
  };

  return [
    uiModel,
    fault,
    loading,
    requestSubmitOrderUpdateCountry,
    interactionData,
  ];
}

export { useSubmitOrderUpdateCountry };
export default useSubmitOrderUpdateCountry;
